/**
 * edj-video-dialog.js (renommé sémantiquement en VAGUE 5 — vidéo inline)
 * Lecteur vidéo inline dans la slide active de la galerie produit.
 * VAGUE 5 Fix 1 — EDJ Shopify theme
 *
 * Plus de lightbox <dialog>. Comportement type Apple.com / YouTube produit :
 * la vidéo REMPLACE l'image principale dans la zone gallery main, en
 * respectant le format défini par CSS (aspect-ratio 4/5).
 *
 * Triggers (VAGUE 6 — bouton "Voir la vidéo" retiré, redondant) :
 *   - [data-edj-slide-play]  (bouton play overlay sur la slide vidéo)
 *   - thumbnail [data-edj-thumb-is-video="1"] (laisse la galerie activer
 *     la slide ; quand la slide vidéo devient active, on injecte la vidéo)
 *
 * Données portées par la slide ou le bouton :
 *   - data-edj-(slide-)video-type   : "native" | "external"
 *   - data-edj-(slide-)video-url    : URL mp4 (si native)
 *   - data-edj-(slide-)video-id     : ID YouTube/Vimeo (si external)
 *   - data-edj-(slide-)video-host   : "youtube" | "vimeo"
 *
 * Au switch de slide (gallery:change), si on quitte une slide vidéo en cours
 * de lecture, on purge le <video>/<iframe> pour stopper la lecture.
 *
 * Pas d'innerHTML avec données utilisateur : tout via setAttribute.
 */
(function () {
  'use strict';

  /* ---------- Helpers ---------- */

  function getSlideVideoData(el) {
    // el peut être : la slide elle-même, le bouton play, ou le bouton "Voir la vidéo"
    var ds = el.dataset || {};
    return {
      type: ds.edjVideoType || ds.edjSlideVideoType || null,
      url: ds.edjVideoUrl || ds.edjSlideVideoUrl || null,
      id: ds.edjVideoId || ds.edjSlideVideoId || null,
      host: ds.edjVideoHost || ds.edjSlideVideoHost || 'youtube'
    };
  }

  function findFirstVideoSlide(gallery) {
    if (!gallery) return null;
    return gallery.querySelector('[data-edj-slide].edj-product-gallery__slide--video');
  }

  function findGallery(el) {
    return el.closest('edj-product-gallery, .edj-product-gallery');
  }

  function buildPlayerEl(data) {
    if (!data || !data.type) return null;
    if (data.type === 'native') {
      if (!data.url) return null;
      var v = document.createElement('video');
      v.setAttribute('controls', '');
      v.setAttribute('autoplay', '');
      v.setAttribute('muted', '');           // requis pour autoplay iOS/Safari
      v.muted = true;
      v.setAttribute('playsinline', '');
      v.setAttribute('preload', 'metadata');
      v.className = 'edj-product-gallery__video';
      v.src = data.url;
      return v;
    }
    if (data.type === 'external') {
      if (!data.id) return null;
      var iframe = document.createElement('iframe');
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('frameborder', '0');
      iframe.className = 'edj-product-gallery__video';
      var src;
      if (data.host === 'vimeo') {
        src = 'https://player.vimeo.com/video/' + encodeURIComponent(data.id) + '?autoplay=1&muted=1&playsinline=1';
      } else {
        src = 'https://www.youtube.com/embed/' + encodeURIComponent(data.id) +
              '?autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1';
      }
      iframe.src = src;
      return iframe;
    }
    return null;
  }

  /**
   * Active la lecture vidéo inline dans la slide vidéo de la galerie.
   * - Récupère les data-* sur la slide
   * - Cache l'image et le bouton play overlay
   * - Insère <video>/<iframe> dans la slide
   * - Marque la slide is-playing pour CSS
   */
  function playInline(slide) {
    if (!slide) return;
    if (slide.dataset.edjVideoActive === '1') return; // déjà actif
    var data = getSlideVideoData(slide);
    var player = buildPlayerEl(data);
    if (!player) return;

    // Ajoute le player dans la slide (en plus de l'image qui sera masquée via CSS)
    slide.appendChild(player);
    slide.dataset.edjVideoActive = '1';
    slide.classList.add('is-playing');
  }

  /**
   * Purge la vidéo de la slide donnée (au quit de la slide ou re-switch).
   */
  function stopInline(slide) {
    if (!slide) return;
    var v = slide.querySelector('video.edj-product-gallery__video');
    if (v) {
      try { v.pause(); v.removeAttribute('src'); v.load(); } catch (_) {}
      v.parentNode && v.parentNode.removeChild(v);
    }
    var iframe = slide.querySelector('iframe.edj-product-gallery__video');
    if (iframe) {
      // Stoppe la lecture en retirant src puis l'iframe
      try { iframe.src = 'about:blank'; } catch (_) {}
      iframe.parentNode && iframe.parentNode.removeChild(iframe);
    }
    slide.dataset.edjVideoActive = '';
    slide.classList.remove('is-playing');
  }

  /**
   * Active une slide vidéo par index (utilisé par le bouton "Voir la vidéo"
   * qui ne sait pas dans quelle slide chercher : on cherche la première slide vidéo).
   */
  function activateAndPlayVideoSlide(gallery, slide) {
    if (!gallery || !slide) return;
    // Demande à la galerie de naviguer vers cette slide via Custom Element
    var idx = parseInt(slide.dataset.index, 10);
    if (Number.isFinite(idx) && typeof gallery.activate === 'function') {
      gallery.activate(idx);
    }
    // Puis injecte la vidéo
    playInline(slide);
  }

  /* ---------- Style helper : masquer l'image et le bouton overlay quand is-playing ---------- */

  function injectStyles() {
    if (document.getElementById('edj-video-inline-style')) return;
    var style = document.createElement('style');
    style.id = 'edj-video-inline-style';
    style.textContent = [
      // Le player remplit la slide en respectant le ratio défini sur __main
      '.edj-product-gallery__video{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;background:#000;display:block;z-index:3;}',
      // Pendant la lecture, on cache l'image et le bouton overlay
      '.edj-product-gallery__slide.is-playing > img,',
      '.edj-product-gallery__slide.is-playing > .edj-product-gallery__image{visibility:hidden;}',
      '.edj-product-gallery__slide.is-playing .edj-product-gallery__slide-play{display:none;}',
      // Pas de cursor zoom-in quand la slide active est une vidéo
      '.edj-product-gallery__main:has(.edj-product-gallery__slide--video.is-active){cursor:default !important;}',
      '.edj-product-gallery__slide--video,',
      '.edj-product-gallery__slide--video img{cursor:default;}',
      // Le bouton play overlay (avant lecture) reste centré
      '.edj-product-gallery__slide-play{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:64px;height:64px;border-radius:50%;border:0;background:rgba(0,0,0,.6);color:#fff;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;z-index:2;}',
      '.edj-product-gallery__slide-play:hover{background:rgba(0,0,0,.85);}',
      '.edj-product-gallery__slide-play-icon{font-size:24px;}',
      '.edj-product-gallery__thumb--video{position:relative;}',
      '.edj-product-gallery__thumb-play{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;text-shadow:0 0 6px rgba(0,0,0,.6);font-size:18px;pointer-events:none;background:rgba(0,0,0,.18);}'
    ].join('');
    document.head.appendChild(style);
  }

  /* ---------- Init ---------- */

  function init() {
    injectStyles();

    // VAGUE 6 — Le bouton "Voir la vidéo" sous l'image a été retiré (redondant
    // avec la thumbnail vidéo qui lance la lecture inline). Plus de binding ici.

    // Bouton play overlay sur la slide vidéo elle-même
    document.querySelectorAll('[data-edj-slide-play]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var slide = btn.closest('[data-edj-slide]');
        if (slide) playInline(slide);
      });
    });

    // Thumbnails vidéo : au 2e click (slide déjà active), lance la vidéo inline.
    // Au 1er click, la galerie active la slide → la vidéo n'est PAS auto-lancée
    // pour préserver le contrôle utilisateur (image preview affichée).
    document.querySelectorAll('[data-edj-thumb-is-video="1"]').forEach(function (thumb) {
      thumb.addEventListener('click', function (e) {
        var gallery = findGallery(thumb);
        var idx = thumb.dataset.index;
        if (!gallery || idx == null) return;
        var slide = gallery.querySelector('[data-edj-slide][data-index="' + CSS.escape(idx) + '"]');
        if (!slide) return;
        if (slide.classList.contains('is-active')) {
          // Déjà sur la slide vidéo → lance lecture
          e.preventDefault();
          e.stopPropagation();
          playInline(slide);
        }
        // Sinon, laisse la galerie activer la slide. L'utilisateur peut cliquer
        // le bouton play central pour lancer.
      });
    });

    // Au changement de slide, purger toute vidéo encore active dans une autre slide
    if (window.EDJ && window.EDJ.events && typeof window.EDJ.events.on === 'function') {
      window.EDJ.events.on('gallery:change', function (payload) {
        if (!payload || payload.index == null) return;
        document.querySelectorAll('[data-edj-slide][data-edj-video-active="1"]').forEach(function (slide) {
          var slideIdx = parseInt(slide.dataset.index, 10);
          if (slideIdx !== payload.index) stopInline(slide);
        });
      });
    } else {
      // Fallback : observer les changements de classe is-active sur les slides
      document.querySelectorAll('[data-edj-slide]').forEach(function (slide) {
        var observer = new MutationObserver(function () {
          if (!slide.classList.contains('is-active') && slide.dataset.edjVideoActive === '1') {
            stopInline(slide);
          }
        });
        observer.observe(slide, { attributes: true, attributeFilter: ['class'] });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
